import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faTimes,
  faUser,
  faEnvelope,
  faMapMarkerAlt,
  faGlobe,
  faStickyNote,
  faPlus,
  faSpinner,
  faExclamationTriangle,
  faEdit,
  faArchive,
  faCalendarAlt,
  faUserTag,
  faCheckCircle,
  faTimesCircle,
  faPause,
  faChartBar,
  faLock,
  faUnlock,
  faFileContract,
  faPhone,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import ArchiveUserModal from '../../components/ArchiveUserModal';
import SuccessModal from '../../components/SuccessModal';
import SearchableSelect from '../../components/SearchableSelect';
import ProfileAvatar from '../../components/ProfileAvatar';
import ProfilePhotoPicker from '../../components/ProfilePhotoPicker';

const InterviewerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [interviewer, setInterviewer] = useState(null);

  // Roles allowed to POST /interviewers/{id}/photo. A prestataire may only do it for their
  // own enquêteurs (the API enforces it too); resp_controle is read-only here.
  const canEditPhoto = (
    ['admin_it', 'chef_terrain', 'chef_terrain_superieur', 'resp_cati', 'resp_admin',
      'charge_etude', 'charge_etude_superieur', 'administrateur_general',
      'administrateur_general_superieur'].includes(currentUser?.role)
    || (currentUser?.role === 'superviseur'
        && interviewer && Number(interviewer.supervisor_id) === Number(currentUser?.id))
  );

  // Photo chosen in the edit form; only sent when « Enregistrer » is clicked.
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLocked, setIsLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  // Per-survey stats (F2F): assigned quota vs realised vs validated (control)
  const [surveyStats, setSurveyStats] = useState([]);
  const [surveyStatsLoading, setSurveyStatsLoading] = useState(false);
  const [statsSurveyFilter, setStatsSurveyFilter] = useState(null); // survey_id or null
  const [statsPage, setStatsPage] = useState(1);
  const STATS_PER_PAGE = 10;
  const [vagueModal, setVagueModal] = useState(null); // { survey_title, quota_projects }
  const vagueDismiss = useModalDismiss(() => setVagueModal(null), { enabled: !!vagueModal });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    zone: '',
    status: 'active',
    ville: [],
    zone_locales: [],
    notes: []
  });
    const [currentVille, setCurrentVille] = useState('');
    const [currentZoneLocale, setCurrentZoneLocale] = useState('');
    const [currentNote, setCurrentNote] = useState('');
    const [cities, setCities] = useState([]);
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [citySearch, setCitySearch] = useState('');
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const [cityDropdownRef, setCityDropdownRef] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confidentialData, setConfidentialData] = useState(null);
  const [confidentialLoading, setConfidentialLoading] = useState(false);

  useEffect(() => {
    fetchInterviewer();
    fetchConfidentialData();
    fetchSurveyStats();
  }, [id]);

  const fetchSurveyStats = async () => {
    try {
      setSurveyStatsLoading(true);
      const response = await api.get(`/interviewers/${id}/survey-stats`);
      setSurveyStats(response.data.data || []);
    } catch (error) {
      console.error('Error fetching survey stats:', error);
    } finally {
      setSurveyStatsLoading(false);
    }
  };

  // Assigné cell click: open the quota. Single project → go directly; multiple vagues → pick one.
  const handleAssignedClick = (s) => {
    const projects = s.quota_projects || [];
    if (projects.length === 0) return;
    if (projects.length === 1) {
      navigate(`/quotas/${projects[0].id}`);
      return;
    }
    setVagueModal({ survey_title: s.survey_title, quota_projects: projects });
  };

  // Réalisé cell click: open F2F productivity prefiltered on this survey + this interviewer.
  const handleRealiseClick = (s) => {
    navigate(`/f2f/productivity?survey_id=${s.survey_id}&interviewer_id=${id}`);
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(String(d).replace(' ', 'T')).toLocaleDateString('fr-FR'); } catch (e) { return String(d).substring(0, 10); }
  };

  const fetchConfidentialData = async () => {
    try {
      setConfidentialLoading(true);
      const response = await api.get(`/confidential-data/interviewer/${id}?masked=false`);
      if (response.data.success) {
        setConfidentialData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching confidential data:', error);
      setConfidentialData(null);
    } finally {
      setConfidentialLoading(false);
    }
  };

  // Check for edit parameter and automatically enter edit mode
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('edit') === 'true') {
      setIsEditing(true);
    }
  }, [location.search]);

  // Fetch cities and regions when editing starts
  useEffect(() => {
    const fetchCitiesAndRegions = async () => {
      if (isEditing) {
        try {
          const [citiesResponse, regionsResponse] = await Promise.all([
            api.get('/cities'),
            api.get('/cities/regions')
          ]);
          
          if (citiesResponse.data.success) {
            setCities(citiesResponse.data.data);
          }
          
          if (regionsResponse.data.success) {
            setRegions(regionsResponse.data.data);
          }
        } catch (error) {
          console.error('Error fetching cities and regions:', error);
        }
      }
    };

    fetchCitiesAndRegions();
  }, [isEditing]);

  const fetchInterviewer = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/interviewers/${id}`);
      if (response.data.success) {
        const interviewerData = response.data.data;
        setInterviewer(interviewerData);
        setIsLocked(interviewerData.is_locked || false);
        setFormData({
          name: interviewerData.name || '',
          email: interviewerData.email || '',
          zone: interviewerData.zone || '',
          status: interviewerData.status || 'active',
          ville: interviewerData.ville || [],
          zone_locales: interviewerData.zone_locales || [],
          notes: interviewerData.notes || []
        });
      } else {
        setErrors({ fetch: 'Enquêteur non trouvé' });
      }
    } catch (error) {
      console.error('Error fetching interviewer:', error);
      setErrors({ fetch: 'Erreur lors du chargement de l\'enquêteur' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleArrayAdd = (field, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // City selection handlers
  const handleCitySelect = (city) => {
    if (!formData.ville.includes(city.name)) {
      setFormData(prev => ({
        ...prev,
        ville: [...prev.ville, city.name]
      }));
    }
    setCitySearch('');
    setShowCityDropdown(false);
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    setCitySearch('');
    setShowCityDropdown(false);
  };

  const getFilteredCities = () => {
    let filtered = cities;
    
    if (selectedRegion) {
      filtered = filtered.filter(city => city.region === selectedRegion);
    }
    
    if (citySearch) {
      filtered = filtered.filter(city => 
        city.name.toLowerCase().includes(citySearch.toLowerCase())
      );
    }
    
    return filtered.slice(0, 10); // Limit to 10 results
  };

  // Click outside handler for city dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef && !cityDropdownRef.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [cityDropdownRef]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/interviewers/${id}`, formData);
      if (response.data.success) {
        let saved = response.data.data;

        // The staged photo is sent once the record itself is saved. A failure here is
        // reported but must not make a successful update look like it failed.
        if (photoFile) {
          try {
            const form = new FormData();
            form.append('photo', photoFile, 'photo.jpg');
            const photoRes = await api.post(`/interviewers/${id}/photo`, form, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            saved = { ...saved, photo_updated_at: photoRes.data?.data?.photo_updated_at || new Date().toISOString() };
          } catch (photoError) {
            console.error('Photo upload failed:', photoError);
            setErrors({ submit: "Les informations ont été enregistrées, mais la photo n'a pas pu l'être." });
          }
          setPhotoFile(null);
        }

        setInterviewer(saved);
        setIsEditing(false);
        // Clear any existing errors
        setErrors({});
        setSuccessMessage('Enquêteur mis à jour avec succès!');
        setShowSuccessModal(true);
        // Remove edit parameter from URL after showing modal
        setTimeout(() => {
          navigate(`/interviewers/${id}`, { replace: true });
        }, 100);
      } else {
        setErrors({ submit: response.data.message || 'Erreur lors de la mise à jour' });
      }
    } catch (error) {
      console.error('Error updating interviewer:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Erreur lors de la mise à jour' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    try {
      setUnlocking(true);
      setErrors({});
      setSuccessMessage('');
      
      await api.post(`/interviewers/${id}/unlock-account`);
      setSuccessMessage('Compte déverrouillé avec succès');
      setShowSuccessModal(true);
      setIsLocked(false);
      setShowUnlockModal(false);
      fetchInterviewer(); // Refresh interviewer data
    } catch (error) {
      console.error('Error unlocking account:', error);
      if (error.response?.data?.message) {
        setErrors({ unlock: error.response.data.message });
      } else {
        setErrors({ unlock: 'Erreur lors du déverrouillage du compte' });
      }
    } finally {
      setUnlocking(false);
    }
  };

  const handleArchiveClick = () => {
    setShowConfirmModal(true);
  };

  // L'appel et la saisie de la raison sont gérés par ArchiveUserModal.
  const handleArchive = () => {
    setSuccessMessage('Enquêteur archivé avec succès!');
    setShowSuccessModal(true);
    setTimeout(() => {
      navigate('/interviewers');
    }, 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />;
      case 'suspended':
        return <FontAwesomeIcon icon={faPause} className="text-red-600" />;
      case 'inactive':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-yellow-600" />;
      default:
        return <FontAwesomeIcon icon={faTimesCircle} className="text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'suspended':
        return 'Suspendu';
      case 'inactive':
        return 'Inactif';
      default:
        return 'Inconnu';
    }
  };

  const getZoneText = (zone) => {
    return zone === 'rural' ? 'Rural' : zone === 'urbain' ? 'Urbain' : 'Non défini';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'enquêteur...</p>
        </div>
      </div>
    );
  }

  if (errors.fetch) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="p-8">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
            <p className="text-gray-600 mb-4">{errors.fetch}</p>
            <button
              onClick={() => navigate('/interviewers')}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retour aux enquêteurs
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only roles that can open the quota builder get clickable "Assigné" cells (e.g. resp_controle can't).
  const canOpenQuota = ['charge_etude', 'charge_etude_superieur', 'chef_terrain', 'chef_terrain_superieur', 'administrateur_general', 'administrateur_general_superieur'].includes(currentUser?.role);

  // Survey-stats filter (applies to the whole dataset) + client-side pagination.
  const statsSurveyOptions = surveyStats.map(s => ({ id: s.survey_id, title: s.survey_title }));
  const filteredStats = statsSurveyFilter
    ? surveyStats.filter(s => String(s.survey_id) === String(statsSurveyFilter))
    : surveyStats;
  const statsTotalPages = Math.max(1, Math.ceil(filteredStats.length / STATS_PER_PAGE));
  const statsCurrentPage = Math.min(statsPage, statsTotalPages);
  const pagedStats = filteredStats.slice((statsCurrentPage - 1) * STATS_PER_PAGE, statsCurrentPage * STATS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-800">
                {isEditing ? 'MODIFICATION ENQUÊTEUR' : 'DÉTAILS ENQUÊTEUR'}
              </h1>
              {interviewer && (
                <div className="ml-4">
                  {interviewer.verified ? (
                    <FontAwesomeIcon 
                      icon={faCheckCircle} 
                      className="text-green-500 text-2xl" 
                      title="Enquêteur vérifié"
                    />
                  ) : (
                    <FontAwesomeIcon 
                      icon={faTimesCircle} 
                      className="text-gray-400 text-2xl" 
                      title="Enquêteur non vérifié"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          {!isEditing && (
            <div className="flex space-x-3">
              {currentUser?.role === 'admin_it' && (
                <button
                  onClick={() => setShowUnlockModal(true)}
                  disabled={!isLocked || unlocking}
                  className={`px-6 py-3 rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center disabled:opacity-50 ${
                    isLocked && !unlocking
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                  title={isLocked ? 'Déverrouiller le compte' : 'Le compte n\'est pas verrouillé'}
                >
                  {unlocking ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={isLocked ? faUnlock : faLock} className="mr-2" />
                  )}
                  {unlocking ? 'Déverrouillage...' : isLocked ? 'Déverrouiller' : 'Déverrouillé'}
                </button>
              )}
              {!['administrateur_general', 'charge_etude', 'charge_etude_superieur'].includes(currentUser?.role) && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Modifier
                  </button>
                  <button
                    onClick={handleArchiveClick}
                    disabled={archiving}
                    className="px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center disabled:opacity-50"
                  >
                    {archiving ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    ) : (
                      <FontAwesomeIcon icon={faArchive} className="mr-2" />
                    )}
                    Archiver
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6">
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {errors.submit}
            </div>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8">
          {isEditing ? (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
              {/* Photo — staged here, uploaded on « Enregistrer » */}
              {canEditPhoto && (
                <ProfilePhotoPicker
                  label="Photo de l'enquêteur"
                  onSelect={setPhotoFile}
                  personType="interviewers"
                  personId={interviewer.id}
                  name={interviewer.name}
                  photoUpdatedAt={interviewer.photo_updated_at}
                  hint="JPG, PNG ou WEBP — 5 Mo maximum. Enregistrée avec le formulaire."
                />
              )}

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-red-600" />
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Entrez le nom complet"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-red-600" />
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="exemple@email.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                {/* Zone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faGlobe} className="mr-2 text-red-600" />
                    Zone
                  </label>
                  <select
                    name="zone"
                    value={formData.zone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                  >
                    <option value="">Sélectionner une zone</option>
                    <option value="rural">Rural</option>
                    <option value="urbain">Urbain</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-red-600" />
                    Statut
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                  >
                    <option value="active">Actif</option>
                    <option value="suspended">Suspendu</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>

              {/* Villes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-red-600" />
                  Villes
                </label>
                
                {/* Region Filter */}
                <div className="mb-3">
                  <select
                    value={selectedRegion}
                    onChange={(e) => handleRegionChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                  >
                    <option value="">Toutes les régions</option>
                    {regions.map((region, index) => (
                      <option key={index} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </div>

                {/* City Search */}
                <div className="relative mb-3" ref={setCityDropdownRef}>
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setShowCityDropdown(true);
                    }}
                    onFocus={() => setShowCityDropdown(true)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    placeholder="Rechercher une ville..."
                  />
                  
                  {/* City Dropdown */}
                  {showCityDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {getFilteredCities().length > 0 ? (
                        getFilteredCities().map((city, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleCitySelect(city);
                            }}
                          >
                            <div className="font-medium">{city.name}</div>
                            <div className="text-sm text-gray-500">{city.region}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500">
                          Aucune ville trouvée
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Cities */}
                {formData.ville.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.ville.map((ville, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {ville}
                        <button
                          type="button"
                          onClick={() => handleArrayRemove('ville', index)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Zone Locales */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-red-600" />
                  Zones Locales
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={currentZoneLocale}
                    onChange={(e) => setCurrentZoneLocale(e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    placeholder="Ajouter une zone locale"
                  />
                  <button
                    type="button"
                    onClick={() => handleArrayAdd('zone_locales', currentZoneLocale, setCurrentZoneLocale)}
                    className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                {formData.zone_locales.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.zone_locales.map((zoneLocale, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                      >
                        {zoneLocale}
                        <button
                          type="button"
                          onClick={() => handleArrayRemove('zone_locales', index)}
                          className="ml-2 text-green-600 hover:text-green-800"
                        >
                          <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faStickyNote} className="mr-2 text-red-600" />
                  Notes
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={currentNote}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    placeholder="Ajouter une note"
                  />
                  <button
                    type="button"
                    onClick={() => handleArrayAdd('notes', currentNote, setCurrentNote)}
                    className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                {formData.notes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.notes.map((note, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800"
                      >
                        {note}
                        <button
                          type="button"
                          onClick={() => handleArrayRemove('notes', index)}
                          className="ml-2 text-yellow-600 hover:text-yellow-800"
                        >
                          <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setPhotoFile(null);
                    setErrors({});
                    setSelectedRegion('');
                    setCitySearch('');
                    setShowCityDropdown(false);
                    // Remove edit parameter from URL
                    navigate(`/interviewers/${id}`, { replace: true });
                    fetchInterviewer(); // Reset form data
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {saving ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Accuracy Score Section */}
              {interviewer.accuracy_rating !== null && interviewer.accuracy_rating !== undefined && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                      Score de Performance
                    </h4>
                    {interviewer.score_updated_at && (
                      <span className="text-xs text-gray-500">
                        Mis à jour: {new Date(interviewer.score_updated_at).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Overall Score - Circular Gauge */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                      <div className="text-sm text-gray-600 mb-4">Score Global</div>
                      <div className="relative w-40 h-40">
                        {/* SVG Circular Gauge */}
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                          {/* Background circle */}
                          <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="12"
                          />
                          {/* Progress circle */}
                          <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke={
                              interviewer.accuracy_rating >= 80 ? '#16a34a' :
                              interviewer.accuracy_rating >= 60 ? '#ca8a04' :
                              '#dc2626'
                            }
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 54}`}
                            strokeDashoffset={`${2 * Math.PI * 54 * (1 - interviewer.accuracy_rating / 100)}`}
                            style={{
                              transition: 'stroke-dashoffset 1.5s ease-in-out',
                            }}
                          />
                        </svg>
                        {/* Center text */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-4xl font-bold text-gray-900">
                            {Math.round(interviewer.accuracy_rating)}
                          </span>
                          <span className="text-sm text-gray-500">/ 100</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-4">Performance globale</div>
                    </div>
                    
                    {/* Productivity Component */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                      <div className="text-sm text-gray-600 mb-4">Productivité</div>
                      <div className="relative w-40 h-40">
                        {/* SVG Circular Gauge */}
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                          {/* Background circle */}
                          <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="12"
                          />
                          {/* Progress circle */}
                          <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="#2563eb"
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 54}`}
                            strokeDashoffset={`${2 * Math.PI * 54 * (1 - (interviewer.prod_component || 0) / 100)}`}
                            style={{
                              transition: 'stroke-dashoffset 1.5s ease-in-out 0.3s',
                            }}
                          />
                        </svg>
                        {/* Center text */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-4xl font-bold text-blue-600">
                            {interviewer.prod_component !== null ? Math.round(interviewer.prod_component) : '-'}
                          </span>
                          <span className="text-sm text-gray-500">/ 100</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-4">Basé sur les complétions</div>
                    </div>
                    
                    {/* Quality Component */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                      <div className="text-sm text-gray-600 mb-4">Qualité</div>
                      <div className="relative w-40 h-40">
                        {/* SVG Circular Gauge */}
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                          {/* Background circle */}
                          <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="12"
                          />
                          {/* Progress circle */}
                          <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="#16a34a"
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 54}`}
                            strokeDashoffset={`${2 * Math.PI * 54 * (1 - (interviewer.qual_component || 0) / 100)}`}
                            style={{
                              transition: 'stroke-dashoffset 1.5s ease-in-out 0.6s',
                            }}
                          />
                        </svg>
                        {/* Center text */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-4xl font-bold text-green-600">
                            {interviewer.qual_component !== null ? Math.round(interviewer.qual_component) : '-'}
                          </span>
                          <span className="text-sm text-gray-500">/ 100</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-4">Basé sur les pénalités</div>
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      <strong>Note:</strong> Le score est calculé automatiquement à partir de 60% Productivité + 40% Qualité, 
                      basé sur les performances des 6 et 18 derniers mois (70% récent, 30% long terme).
                    </p>
                  </div>
                </div>
              )}

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <ProfileAvatar
                    personType="interviewers"
                    personId={interviewer.id}
                    name={interviewer.name}
                    photoUpdatedAt={interviewer.photo_updated_at}
                    size="lg"
                    className="ring-2 ring-white shadow"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{interviewer.name}</h3>
                    <p className="text-sm text-gray-500">Enquêteur</p>
                  </div>
                </div>
                {interviewer.ref && (
                  <div className="flex items-center space-x-3">
                    <FontAwesomeIcon icon={faUserTag} className="text-gray-400" />
                    <span className="text-gray-700">Référence: {interviewer.ref}</span>
                  </div>
                )}
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                  <span className="text-gray-700">{interviewer.email}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                  {confidentialLoading ? (
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Chargement...
                    </span>
                  ) : (
                    <span className="text-gray-700">
                      {confidentialData?.phone || <span className="text-gray-400 italic">Non renseigné</span>}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faGlobe} className="text-gray-400" />
                  <span className="text-gray-700">{getZoneText(interviewer.zone)}</span>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusIcon(interviewer.status)}
                  <span className="text-gray-700">{getStatusText(interviewer.status)}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
                  <span className="text-gray-700">
                    Créé le {formatDate(interviewer.created_at)}
                  </span>
                </div>
                {!interviewer.is_cati && (interviewer.supervisor?.name || interviewer.supervisor_name) && (
                  <div className="flex items-center space-x-3">
                    <FontAwesomeIcon icon={faUserTag} className="text-gray-400" />
                    <span className="text-gray-700">
                      Prestataire:{' '}
                      {(interviewer.supervisor?.id || interviewer.supervisor_id) ? (
                        <Link
                          to={`/supervisors/${interviewer.supervisor?.id || interviewer.supervisor_id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {interviewer.supervisor?.name || interviewer.supervisor_name}
                        </Link>
                      ) : (
                        <span>{interviewer.supervisor?.name || interviewer.supervisor_name}</span>
                      )}
                    </span>
                  </div>
                )}
                {/* Contract Information - CATI only */}
                {interviewer.is_cati && <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFileContract} className="text-gray-400" />
                  <div>
                    {(interviewer.contrat_signed === true || interviewer.contrat_signed === 1) ? (
                      <div>
                        <span className="text-gray-700 font-medium text-green-600">Contrat signé</span>
                        {interviewer.contract_start_date && (
                          <p className="text-xs text-gray-500">
                            Début: {new Date(interviewer.contract_start_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                        {interviewer.contract_end_date && (
                          <p className="text-xs text-gray-500">
                            Fin: {new Date(interviewer.contract_end_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">Contrat non signé</span>
                    )}
                  </div>
                </div>}
              </div>

              {/* Villes */}
              {Array.isArray(interviewer.ville) && interviewer.ville.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-red-600" />
                    Villes
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {interviewer.ville.map((ville, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {ville}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Zone Locales */}
              {Array.isArray(interviewer.zone_locales) && interviewer.zone_locales.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-red-600" />
                    Zones Locales
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {interviewer.zone_locales.map((zoneLocale, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                      >
                        {zoneLocale}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {Array.isArray(interviewer.notes) && interviewer.notes.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    <FontAwesomeIcon icon={faStickyNote} className="mr-2 text-red-600" />
                    Notes
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {interviewer.notes.map((note, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800"
                      >
                        {note}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Per-survey stats: assigned quota vs realised vs validated (control) — F2F only, hidden for supervisors */}
        {!isEditing && interviewer && !interviewer.is_cati && currentUser?.role !== 'superviseur' && (
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faChartBar} className="text-red-600 mr-2" />
                Statistiques par enquête
              </h3>
              {surveyStats.length > 0 && (
                <div className="w-full sm:w-64 text-sm">
                  <SearchableSelect
                    options={statsSurveyOptions}
                    selectedValue={statsSurveyFilter}
                    onChange={(sid) => { setStatsSurveyFilter(sid); setStatsPage(1); }}
                    getOptionLabel={(o) => o.title}
                    showOptionMeta={false}
                    placeholder="Toutes les enquêtes"
                    searchPlaceholder="Rechercher une enquête..."
                    triggerClassName="px-3 py-2"
                  />
                </div>
              )}
            </div>
            {surveyStatsLoading ? (
              <div className="flex justify-center py-6">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 text-2xl" />
              </div>
            ) : filteredStats.length === 0 ? (
              <p className="text-center text-gray-500 py-6 text-sm">
                {surveyStats.length === 0 ? 'Aucune donnée pour cet enquêteur.' : 'Aucune enquête ne correspond au filtre.'}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                        <th className="py-2 pr-3">Enquête</th>
                        <th className="py-2 px-3 text-center" title="Quota assigné à l'enquêteur">Assigné (quota)</th>
                        <th className="py-2 px-3 text-center" title="Complétions réalisées">Réalisé</th>
                        <th className="py-2 px-3 text-center" title="Soumissions validées au contrôle">Validé (contrôle)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pagedStats.map((s) => (
                        <tr key={s.survey_id} className="hover:bg-gray-50">
                          <td className="py-2 pr-3 font-medium text-gray-900">{s.survey_title}</td>
                          <td className="py-2 px-3 text-center">
                            {canOpenQuota && s.quota_projects && s.quota_projects.length > 0 ? (
                              <button
                                onClick={() => handleAssignedClick(s)}
                                className="font-medium text-red-600 hover:text-red-800 hover:underline"
                                title="Ouvrir le quota"
                              >
                                {s.assigned}
                              </button>
                            ) : (
                              <span className="text-gray-800">{s.assigned}</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button
                              onClick={() => handleRealiseClick(s)}
                              title="Voir la productivité"
                              className={`font-semibold hover:underline ${
                                s.completed > s.assigned
                                  ? 'text-red-600 hover:text-red-800'
                                  : (s.completed === s.assigned && s.assigned > 0 ? 'text-green-600 hover:text-green-800' : 'text-gray-800 hover:text-gray-900')
                              }`}
                            >
                              {s.completed}
                            </button>
                          </td>
                          <td className="py-2 px-3 text-center text-gray-800">{s.validated}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
                        <td className="py-2 pr-3">Total{statsSurveyFilter ? '' : ` (${filteredStats.length})`}</td>
                        <td className="py-2 px-3 text-center">{filteredStats.reduce((a, s) => a + s.assigned, 0)}</td>
                        <td className="py-2 px-3 text-center">{filteredStats.reduce((a, s) => a + s.completed, 0)}</td>
                        <td className="py-2 px-3 text-center">{filteredStats.reduce((a, s) => a + s.validated, 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {statsTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="text-gray-500">Page {statsCurrentPage} / {statsTotalPages}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setStatsPage(p => Math.max(1, p - 1))}
                        disabled={statsCurrentPage <= 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} className="text-xs" /> Précédent
                      </button>
                      <button
                        onClick={() => setStatsPage(p => Math.min(statsTotalPages, p + 1))}
                        disabled={statsCurrentPage >= statsTotalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        Suivant <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Vague picker: which quota (wave) to open for a tracker survey */}
      {vagueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...vagueDismiss}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Choisir une vague ({vagueModal.quota_projects.length})</h3>
              <button onClick={() => setVagueModal(null)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <div className="px-6 py-3 overflow-y-auto flex-1 min-h-0">
              <p className="text-sm text-gray-600 mb-3">{vagueModal.survey_title} — sélectionnez le quota à ouvrir :</p>
              <ul className="divide-y divide-gray-100">
                {vagueModal.quota_projects.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">{p.wave_label || `Quota #${p.id}`}</div>
                      <div className="text-xs text-gray-500">
                        {p.date_debut || p.date_fin ? `${fmtDate(p.date_debut)} → ${fmtDate(p.date_fin)}` : 'Aucune période'}
                      </div>
                    </div>
                    <button
                      onClick={() => { setVagueModal(null); navigate(`/quotas/${p.id}`); }}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 flex-shrink-0"
                    >
                      Ouvrir
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button onClick={() => setVagueModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      <ArchiveUserModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onArchive={handleArchive}
        user={interviewer || {}}
        archiveEndpoint={`/interviewers/${id}`}
        archiveMethod="DELETE"
        title="Archiver l'enquêteur"
        subtitle="Cette action enverra l'enquêteur vers les archives"
        entityLabel="enquêteur"
        consequences={[
          'Son compte sera désactivé : il ne pourra plus se connecter.',
          'Son historique est conservé (contrôles le concernant, productivité, paiements).',
          'Vous pourrez le restaurer à tout moment depuis la page des archives.',
        ]}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Opération terminée"
        message={successMessage}
      />
      
      <ConfirmationModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onConfirm={handleUnlock}
        title="Déverrouiller le compte"
        message="Êtes-vous sûr de vouloir déverrouiller ce compte ? Les tentatives de connexion échouées seront réinitialisées."
        confirmText="Déverrouiller"
        cancelText="Annuler"
        confirmColor="bg-orange-600 hover:bg-orange-700"
        loading={unlocking}
      />
    </div>
  );
};

export default InterviewerDetail;
